import { DependencyInjector } from '../injector';
import { Type } from '@angular/core';
import { CurrencyPipe } from '../../currency/pipes/currency.pipe';
import { FormInterface } from '@kaiser-software/chassis-core/src/app/forms/form/models/form.model';

/**
 * Get an Angular service instance from the Angular injector.
 *
 * @param {Type<T>} token
 * @returns {T}
 */
export function app<T>(token: Type<T>): T {
    return DependencyInjector.get(token);
}

/*
 * Text helpers
 */

/**
 * Add leading characters to a string if the given value does not
 * have the provided length.
 *
 * @param {string | number} value
 * @param {number} length
 * @param {string} string
 * @return {string}
 */
export function leftPad(value: string | number, length: number, string: string) {
    if (!Number.isInteger(length)) {
        throw new Error('Length should be an integer');
    }

    return String(value).padStart(length, string);
}

/**
 * Add trailing characters to a string if the given value does not
 * have the provided length.
 *
 * @param {string | number} value
 * @param {number} length
 * @param {string} string
 * @return {string}
 */
export function rightPad(value: string | number, length: number, string: string) {
    if (!Number.isInteger(length)) {
        throw new Error('Length should be an integer');
    }

    return String(value).padEnd(length, string);
}

/**
 * Retrieve a value from a nested array or object using the dot notation.
 *
 * @param data
 * @param property
 */
export function dataGet(data: Object, property: string): any {
    return property.split('.').reduce((a, b) => a ? a[b] : undefined, data);
}

/*
 * Math and calculation helpers
 */

/**
 * Add tax by rate to the specified value.
 *
 * @param {number} value
 * @param {number} rate
 * @returns {number}
 */
export function addTax(value: number, rate: number) {
    return Number(value) * (100 + Number(rate)) / 100;
}

/**
 * Subtract tax by rate from the specified value.
 *
 * @param {number} value
 * @param {number} rate
 * @returns {number}
 */
export function subtractTax(value: number, rate: number) {
    return Number(value) / (100 + Number(rate)) * 100;
}

/**
 * Get tax by rate from the specified value.
 *
 * @param {number} value
 * @param {number} rate
 * @returns {number}
 */
export function getTaxValue(value: number, rate: number) {
    return Number(value) * 1000 / 100 * Number(rate) / 1000;
}

/**
 * Subtract discount by value from the specified value.
 *
 * @param {number} value
 * @param {number} discountValue
 * @returns {number}
 */
export function subtractDiscountByValue(value: number, discountValue: number) {
    return Number(value) - discountValue;
}

/**
 * Subtract discount by percentage from the specified value.
 *
 * @param {number} value
 * @param {number} discountPercentage
 * @returns {number}
 */
export function subtractDiscountByPercentage(value: number, discountPercentage: number) {
    return Number(value) * (100 - Number(discountPercentage)) / 100;
}

export function money(value: number | string, toCurrency = null, formatOnly = false, showSymbol = true) {
    const currencyPipe = DependencyInjector.get(CurrencyPipe);
    return currencyPipe.transform(value, toCurrency, formatOnly, showSymbol);
}

export function convert(value: number | string, toCurrency = null, fromCurrency = null, toCurrencyRate = null, fromCurrencyRate = null) {
    const currencyPipe = DependencyInjector.get(CurrencyPipe);
    return currencyPipe.convert(value, toCurrency, fromCurrency, toCurrencyRate, fromCurrencyRate);
}

export function getCurrency(code: string) {
    const currencyPipe = DependencyInjector.get(CurrencyPipe);
    return currencyPipe.getCurrency(code);
}

export function getDefaultCurrency() {
    const currencyPipe = DependencyInjector.get(CurrencyPipe);
    return currencyPipe.getDefaultCurrency();
}

/**
 * Rounds a number.
 *
 * @param {number} value
 * @param {number} decimals
 * @return {number}
 */
export function round(value: number, decimals: number = 0) {
    return Number(Math.round(Number(value + 'e' + decimals)) + 'e-' + decimals);
}

/**
 * Sort an array of objects based on the given property
 *
 * Example:
 * var people = [
 *      {name: "John", surname: "Doe"},
 *      {name:"Kaiser", surname:"Software"}
 * ];
 *
 * people.sort(dynamicSort('name')); // Sort ascending
 * people.sort(dynamicSort('-name')); // Sort descending
 *
 * @param property
 * @return {(a, b) => number}
 */
export function dynamicSort(property) {
    let sortOrder = 1;
    if (property[0] === "-") {
        sortOrder = -1;
        property  = property.substr(1);
    }
    return function (a, b) {
        const result = (a[property] < b[property]) ? -1 : (a[property] > b[property]) ? 1 : 0;
        return result * sortOrder;
    }
}

/**
 * Sort an array of objects based on the given properties
 *
 * Example:
 * var people = [
 *      {name: "John", surname: "Doe"},
 *      {name:"Kaiser", surname:"Software"}
 * ];
 *
 * people.sort(dynamicSortMultiple('name', 'surname)); // Sort ascending, ascending
 * people.sort(dynamicSortMultiple('name', '-surname')); // Sort ascending, descending
 * @return {(obj1, obj2) => number}
 */
export function dynamicSortMultiple(...args: any[]) {
    const numberOfProperties = args.length;
    return function (obj1, obj2) {
        let i      = 0;
        let result = 0;
        /* try getting a different result from 0 (equal)
         * as long as we have extra properties to compare
         */
        while (result === 0 && i < numberOfProperties) {
            result = dynamicSort(args[i])(obj1, obj2);
            i++;
        }
        return result;
    }
}

/**
 * Change an element object in a given form
 *
 * @param form
 * @param name
 * @param elementObject
 */
export function changeElementInForm(form: FormInterface | any, name: string, elementObject: any) {

    form.groups.forEach(group => {
        group.sections.forEach(section => {
            section.rows.forEach(row => {
                const match = row.elements.find(element => element.name === name);

                console.log('match found?', match);

                if (match) {
                    Object.assign(match, elementObject);
                }
            });
        });
    });

    return form;
}

export function range(start: number, end: number): Array<number> {
    return Array.from({length: 1 + (end - start)}, (v, k) => k + start);
}


// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/support/helpers/helpers.ts